'use client';

import { useState } from 'react';
import { 
  ChartBarIcon, 
  UsersIcon, 
  CogIcon, 
  BellIcon,
  ArrowTrendingUpIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  change?: string;
}

const StatCard = ({ title, value, icon, change }: StatCardProps) => (
  <div className="bg-white rounded-lg shadow p-6">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-600">{title}</p>
        <p className="text-2xl font-semibold text-gray-900 mt-1">{value}</p>
        {change && (
          <p className="text-sm text-green-600 mt-1">{change}</p>
        )}
      </div>
      <div className="p-3 bg-blue-50 rounded-full">
        {icon}
      </div>
    </div>
  </div>
);

export default function AdminDashboard() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Top Navigation */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-semibold text-gray-900">Admin Dashboard</h1>
            </div>
            <div className="flex items-center space-x-4">
              <button className="p-2 text-gray-400 hover:text-gray-500">
                <BellIcon className="h-6 w-6" />
              </button>
              <button className="p-2 text-gray-400 hover:text-gray-500">
                <CogIcon className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Total Users"
            value="1,234"
            icon={<UsersIcon className="h-6 w-6 text-blue-600" />}
            change="+12% from last month"
          />
          <StatCard
            title="Revenue"
            value="$12,345"
            icon={<CurrencyDollarIcon className="h-6 w-6 text-green-600" />}
            change="+8% from last month"
          />
          <StatCard
            title="Active Sessions"
            value="456"
            icon={<ChartBarIcon className="h-6 w-6 text-purple-600" />}
            change="+5% from last month"
          />
          <StatCard
            title="Growth"
            value="23%"
            icon={<ArrowTrendingUpIcon className="h-6 w-6 text-orange-600" />}
            change="+2% from last month"
          />
        </div>

        {/* Main Content Area */}
        <div className="mt-8 grid grid-cols-1 gap-5 lg:grid-cols-2">
          {/* Recent Activity */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Recent Activity</h2>
            <div className="space-y-4">
              {/* Placeholder for activity items */}
              <p className="text-gray-500">Activity data will be loaded here...</p>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h2>
            <div className="grid grid-cols-2 gap-4">
              <button className="p-4 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100">
                Add New User
              </button>
              <button className="p-4 bg-green-50 text-green-700 rounded-lg hover:bg-green-100">
                Generate Report
              </button>
              <button className="p-4 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100">
                View Analytics
              </button>
              <button className="p-4 bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100">
                Manage Settings
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
} 